// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "@reach/router";
import { Button, Divider, Form, message, Modal, Popconfirm } from "antd";
import { QuestionCircleOutlined, TeamOutlined } from "@ant-design/icons";
import { SurveyWriteRequestDTO } from "../../../../api/backend-openapi";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { ValidatorResult } from "../../../types/Types";
import { requestCreateSurvey } from "../../../../state/survey/SurveysEffects";
import { addSurvey } from "../../../../state/survey/SurveysEvents";
import { Question, SurveyWorkflowStatus } from "../../../types/SurveyTypes";
import { validate } from "../../../../validator/SurveyValidator";
import SurveySettingsFormSection from "../details/SurveySettingsFormSection";
import { QuestionBlocks } from "../block/QuestionBlocks";
import SurveyDateFormSection from "../details/SurveyDateFormSection";
import { displayError } from "../../../../utils/displayError";

const { confirm } = Modal;

const AddSurveyForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [questions, setQuestions] = useState<Question[]>([]);

  const [sendPushValueToDisplay, setSendPushValueToDisplay] = useState(true);

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: t("add_survey.modal.description"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    return requestCreateSurvey.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addSurvey(result.data);
        navigate(`/surveys/${result.data.id}`);
        message.success(t("add_survey.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const handleSubmit = (values: any, status: SurveyWorkflowStatus) => {
    const validationResult: ValidatorResult = validate(questions);
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed) {
          questions.forEach(question => {
            // TODO déterminer si on veut utiliser les ID de question générés sur le front ou écraser avec le back
            delete question.id;
            delete question.templateId;
            // TODO déterminer si on veut utiliser les ID de réponse générés sur le front ou écraser avec le back
            question.answerChoice.forEach(answer => delete answer.id);
          });
          const surveyToAdd: SurveyWriteRequestDTO = {
            title: values.title,
            type: values.type,
            scheduledAvailabilityDate: values.scheduledAvailabilityDate,
            scheduledAvailabilityEndDate: values.scheduledAvailabilityEndDate,
            scheduledVoteCloseDate: values.scheduledVoteCloseDate,
            scheduledResultAvailabilityDate:
              values.scheduledResultAvailabilityDate,
            questions: questions,
            status,
            branch: values.branch,
            sendPush: sendPushValueToDisplay,
            audience: values.audience,
            broadcastListId: values.broadcastListId
          };

          requestCreateSurvey(surveyToAdd);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values, SurveyWorkflowStatus.SAVED);
  };

  const handleSubmitToValidation = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, SurveyWorkflowStatus.VALIDATION);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSave}
      onFinishFailed={onFinishFailed}
      form={form}
    >
      <div className="form-first-item-sticky-top-container">
        <div className="form-first-item-sticky-top-content">
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">
              {t("survey.survey_actions")}
            </h3>
          </Divider>
          <Form.Item className="text-center mb-0">
            <Popconfirm
              title={t("add_survey.cancel_confirmation")}
              okText={t("global.yes")}
              cancelText={t("global.no")}
              onConfirm={() => {
                navigate("/surveys");
                form.resetFields();
              }}
              placement="top"
              icon={<QuestionCircleOutlined />}
            >
              <Button htmlType="reset" className="m-2">
                <StopOutlined /> {t("add_survey.cancel")}
              </Button>
            </Popconfirm>
            <Button
              className="btn-primary m-2"
              htmlType="submit"
              loading={buttonLoading}
            >
              <SaveOutlined /> {t("global.save")}
            </Button>
            <Button
              className="btn-success m-2"
              onClick={handleSubmitToValidation}
              loading={buttonLoading}
            >
              <TeamOutlined /> {t("edit_survey.validation")}
            </Button>
          </Form.Item>
        </div>
      </div>
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">
          {t("survey.responses_parameters")}
        </h3>
      </Divider>
      <SurveyDateFormSection editMode={true} showRequired={true} />
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("survey.settings")}</h3>
      </Divider>
      <SurveySettingsFormSection
        showRequired={true}
        editMode={true}
        sendPushValueToDisplay={sendPushValueToDisplay}
        setSendPushValueToDisplay={setSendPushValueToDisplay}
      />
      <QuestionBlocks questions={questions} setQuestions={setQuestions} />
    </Form>
  );
};

export default AddSurveyForm;
